// frontend/src/pages/shops/MultiLocationRequests.js
// Owner Portal: View and manage multi-location requests from professionals

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SidebarLayout from './SidebarLayout';
import './shopStyles.css';

function MultiLocationRequests() {
  const [requests, setRequests] = useState([]);
  const [approvedProfessionals, setApprovedProfessionals] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' or 'approved'

  useEffect(() => {
    fetchRequests();
    fetchApprovedProfessionals();
    fetchLocations();
  }, []);

  const fetchRequests = async () => {
    try {
      const token = localStorage.getItem('ogestyle_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/multi-location/requests`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRequests(response.data.requests || []);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load requests');
      setLoading(false);
    }
  };

  const fetchApprovedProfessionals = async () => {
    try {
      const token = localStorage.getItem('ogestyle_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/multi-location/approved-professionals`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setApprovedProfessionals(response.data.professionals || []);
    } catch (err) {
    }
  };

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem('ogestyle_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/shop/my-shops`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLocations(response.data.locations || []);
    } catch (err) {
    }
  };

  const handleApprove = (request) => {
    setSelectedRequest(request);
    // Pre-select current location
    setSelectedLocations([request.currentLocation?._id].filter(Boolean));
  };

  const handleLocationToggle = (locationId) => {
    setSelectedLocations(prev => {
      if (prev.includes(locationId)) {
        return prev.filter(id => id !== locationId);
      } else {
        return [...prev, locationId];
      }
    });
  };

  const confirmApproval = async () => {
    if (selectedLocations.length === 0) {
      alert('Please select at least one location');
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('ogestyle_token');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/multi-location/approve/${selectedRequest.staffId}`,
        { approvedLocationIds: selectedLocations },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Multi-location access approved!');
      setSelectedRequest(null);
      setSelectedLocations([]);
      fetchRequests(); // Refresh list
      fetchApprovedProfessionals(); // Refresh approved list
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to approve request');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeny = async (staffId) => {
    if (!window.confirm('Are you sure you want to deny this request?')) {
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('ogestyle_token');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/multi-location/deny/${staffId}`,
        { reason: 'Request denied by owner' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Request denied');
      fetchRequests(); // Refresh list
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to deny request');
    } finally {
      setProcessing(false);
    }
  };

  const handleRevoke = async (staffId, professionalName) => {
    if (!window.confirm(`Are you sure you want to revoke multi-location access for ${professionalName}? This will remove their schedules at all locations except their primary location.`)) {
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('ogestyle_token');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/multi-location/disable/${staffId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Multi-location access revoked successfully');
      fetchApprovedProfessionals(); // Refresh approved list
      fetchRequests(); // Refresh pending list in case they resubmit
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to revoke access');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="container" style={{ padding: '40px 20px', textAlign: 'center' }}>
          <div className="spinner"></div>
          <p>Loading requests...</p>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="container" style={{ padding: '40px 20px', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '8px' }}>
          Multi-Location Management
        </h1>
        <p style={{ color: '#666', fontSize: '16px' }}>
          Manage professionals' access to work at multiple locations
        </p>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '8px', 
        marginBottom: '24px',
        borderBottom: '2px solid #e0e0e0'
      }}>
        <button
          onClick={() => setActiveTab('pending')}
          style={{
            padding: '12px 24px',
            background: activeTab === 'pending' ? '#fff' : 'transparent',
            border: 'none',
            borderBottom: activeTab === 'pending' ? '3px solid #3b82f6' : '3px solid transparent',
            color: activeTab === 'pending' ? '#3b82f6' : '#666',
            fontWeight: activeTab === 'pending' ? '600' : '400',
            fontSize: '16px',
            cursor: 'pointer',
            marginBottom: '-2px'
          }}
        >
          Pending Requests {requests.length > 0 && `(${requests.length})`}
        </button>
        <button
          onClick={() => setActiveTab('approved')}
          style={{
            padding: '12px 24px',
            background: activeTab === 'approved' ? '#fff' : 'transparent',
            border: 'none',
            borderBottom: activeTab === 'approved' ? '3px solid #3b82f6' : '3px solid transparent',
            color: activeTab === 'approved' ? '#3b82f6' : '#666',
            fontWeight: activeTab === 'approved' ? '600' : '400',
            fontSize: '16px',
            cursor: 'pointer',
            marginBottom: '-2px'
          }}
        >
          Approved Professionals {approvedProfessionals.length > 0 && `(${approvedProfessionals.length})`}
        </button>
      </div>

      {error && (
        <div style={{
          padding: '16px',
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          borderRadius: '8px',
          marginBottom: '24px',
          color: '#c33'
        }}>
          {error}
        </div>
      )}

      {/* Pending Requests Tab */}
      {activeTab === 'pending' && (
        <>
          {requests.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              backgroundColor: '#f9f9f9',
              borderRadius: '12px'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
              <h3 style={{ fontSize: '20px', marginBottom: '8px', color: '#333' }}>
                No Pending Requests
              </h3>
              <p style={{ color: '#666', fontSize: '14px' }}>
                When professionals request multi-location access, they'll appear here.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {requests.map(request => (
            <div
              key={request.staffId}
              style={{
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                <div>
                  <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '4px' }}>
                    {request.professional.firstName} {request.professional.lastName}
                  </h3>
                  <p style={{ color: '#666', fontSize: '14px' }}>
                    {request.professional.email}
                  </p>
                </div>
                <span style={{
                  padding: '6px 12px',
                  backgroundColor: '#fff3cd',
                  color: '#856404',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  PENDING
                </span>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                  <strong>Current Location:</strong> {request.currentLocation?.name || 'Not assigned'}
                  {request.currentLocation?.city && request.currentLocation?.state && 
                    ` (${request.currentLocation.city}, ${request.currentLocation.state})`
                  }
                </div>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                  <strong>Professional Type:</strong> {request.professionalType || 'N/A'}
                </div>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  <strong>Requested:</strong> {new Date(request.requestedAt).toLocaleDateString()}
                </div>
              </div>

              {/* Always show request note section */}
              <div style={{
                padding: '12px',
                backgroundColor: request.requestNote ? '#f8f9fa' : '#fefce8',
                borderLeft: `4px solid ${request.requestNote ? '#007bff' : '#eab308'}`,
                borderRadius: '4px',
                marginBottom: '16px'
              }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px', fontWeight: '600' }}>
                  Request Note:
                </div>
                <p style={{ fontSize: '14px', color: '#333', margin: 0, fontStyle: request.requestNote ? 'normal' : 'italic' }}>
                  {request.requestNote || 'No note provided by professional'}
                </p>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => handleApprove(request)}
                  disabled={processing}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#28a745',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: processing ? 'not-allowed' : 'pointer',
                    opacity: processing ? 0.6 : 1
                  }}
                >
                  Approve
                </button>
                <button
                  onClick={() => handleDeny(request.staffId)}
                  disabled={processing}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#dc3545',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: processing ? 'not-allowed' : 'pointer',
                    opacity: processing ? 0.6 : 1
                  }}
                >
                  Deny
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
        </>
      )}

      {/* Approved Professionals Tab */}
      {activeTab === 'approved' && (
        <>
          {approvedProfessionals.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              backgroundColor: '#f9f9f9',
              borderRadius: '12px'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
              <h3 style={{ fontSize: '20px', marginBottom: '8px', color: '#333' }}>
                No Approved Professionals
              </h3>
              <p style={{ color: '#666', fontSize: '14px' }}>
                Professionals you approve for multi-location access will appear here.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {approvedProfessionals.map(prof => (
                <div
                  key={prof.staffId}
                  style={{
                    backgroundColor: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: '12px',
                    padding: '24px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                    <div>
                      <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '4px' }}>
                        {prof.professional.firstName} {prof.professional.lastName}
                      </h3>
                      <p style={{ color: '#666', fontSize: '14px' }}>
                        {prof.professional.email}
                      </p>
                    </div>
                    <span style={{
                      padding: '6px 12px',
                      backgroundColor: '#d1fae5',
                      color: '#065f46',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      ✓ APPROVED
                    </span>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                      <strong>Primary Location:</strong> {prof.primaryLocation?.name || 'Not assigned'}
                      {prof.primaryLocation?.city && prof.primaryLocation?.state && 
                        ` (${prof.primaryLocation.city}, ${prof.primaryLocation.state})`
                      }
                    </div>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                      <strong>Professional Type:</strong> {prof.professionalType || 'N/A'}
                    </div>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}>
                      <strong>Approved:</strong> {prof.approvedAt ? new Date(prof.approvedAt).toLocaleDateString() : 'N/A'}
                    </div>
                    
                    <div style={{ marginTop: '12px' }}>
                      <strong style={{ fontSize: '14px', color: '#333' }}>Approved Locations ({prof.approvedLocations?.length || 0}):</strong>
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {prof.approvedLocations && prof.approvedLocations.length > 0 ? (
                          prof.approvedLocations.map(loc => (
                            <div 
                              key={loc._id}
                              style={{
                                padding: '8px 12px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '6px',
                                fontSize: '13px',
                                color: '#333'
                              }}
                            >
                              📍 {loc.name || 'Unnamed Location'}{loc.city && loc.state ? ` (${loc.city}, ${loc.state})` : ''}
                            </div>
                          ))
                        ) : (
                          <div style={{ fontSize: '13px', color: '#999', fontStyle: 'italic' }}>
                            No locations approved
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handleRevoke(prof.staffId, `${prof.professional.firstName} ${prof.professional.lastName}`)}
                    disabled={processing}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#dc3545',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: processing ? 'not-allowed' : 'pointer',
                      opacity: processing ? 0.6 : 1
                    }}
                  >
                    Revoke Multi-Location Access
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Approval Modal */}
      {selectedRequest && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
              Approve Multi-Location Access
            </h2>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '24px' }}>
              Select which locations {selectedRequest.professional.firstName} can work at
            </p>

            <div style={{ marginBottom: '24px' }}>
              {locations.map(location => (
                <label
                  key={location._id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    cursor: 'pointer',
                    backgroundColor: selectedLocations.includes(location._id) ? '#e7f3ff' : '#fff'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedLocations.includes(location._id)}
                    onChange={() => handleLocationToggle(location._id)}
                    style={{ marginRight: '12px', width: '18px', height: '18px' }}
                  />
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px' }}>
                      {location.name || location.businessName}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {location.street}, {location.city}, {location.state}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={confirmApproval}
                disabled={processing || selectedLocations.length === 0}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#28a745',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: (processing || selectedLocations.length === 0) ? 'not-allowed' : 'pointer',
                  opacity: (processing || selectedLocations.length === 0) ? 0.6 : 1
                }}
              >
                {processing ? 'Approving...' : 'Approve Multi-Location'}
              </button>
              <button
                onClick={() => {
                  setSelectedRequest(null);
                  setSelectedLocations([]);
                }}
                disabled={processing}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: processing ? 'not-allowed' : 'pointer',
                  opacity: processing ? 0.6 : 1
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  </SidebarLayout>
  );
}

export default MultiLocationRequests;
